import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Mail, Trash2, Users, HeartHandshake, Eye, Pencil, Send } from "lucide-react";
import { HeaderAccent } from "@/lib/status";

const EMPTY = { name: "", email: "", password: "", client_id: "" };

function EditFamily({ f, clients, onClose, onSaved }) {
  const [form, setForm] = useState({ name: f.name, email: f.email, client_id: f.client_id || "", password: "" });
  const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
  const save = async (e) => {
    e.preventDefault();
    try { await api.put(`/family/${f.id}`, form); toast.success("Family login updated"); onSaved(); } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form onSubmit={save} data-testid="edit-family-form" className="relative z-10 w-full max-w-md space-y-4 rounded-xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between"><h2 className="font-heading text-xl font-bold text-ink">Edit family login</h2><button type="button" onClick={onClose}><X size={20} className="text-ink-muted" /></button></div>
        <label className="block text-sm font-medium text-ink">Client<select className={input} value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="edit-family-client">{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
        <label className="block text-sm font-medium text-ink">Family member name<input required className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-family-name" /></label>
        <label className="block text-sm font-medium text-ink">Real email address<input required type="email" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-family-email" /></label>
        <label className="block text-sm font-medium text-ink">New password (optional)<input className={input} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Leave blank to keep current" data-testid="edit-family-password" /></label>
        <button type="submit" data-testid="save-edit-family" className="w-full rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark">Save changes</button>
      </form>
    </div>
  );
}

export default function FamilyAdmin() {
  const [families, setFamilies] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [edit, setEdit] = useState(null);
  const welcome = async (f) => {
    try { await api.post(`/family/${f.id}/welcome`); toast.success(`Welcome email sent to ${f.email}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const load = () => api.get("/family").then((r) => setFamilies(r.data));
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error("Select the client this family belongs to"); return; }
    try { await api.post("/family", form); toast.success("Family login created"); setOpen(false); setForm(EMPTY); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Remove ${f.name}?`)) return;
    try { await api.delete(`/family/${f.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

  return (
    <div className="space-y-6" data-testid="family-admin-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Family access</h1>
          <p className="mt-1 text-ink-soft">Give a client's family a simple login to follow visits & summaries.</p>
          <HeaderAccent />
        </div>
        <button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="add-family-button"
          className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-dark">
          <Plus size={18} /> Create family login
        </button>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {families.map((f) => (
          <div key={f.id} data-testid={`family-card-${f.id}`} className="rounded-xl border border-border bg-white p-6 shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-sage-light text-sage-dark"><HeartHandshake size={20} /></div>
                <div>
                  <h3 className="font-heading font-semibold text-ink">{f.name}</h3>
                  <span className="text-xs text-ink-muted">Family of {f.client_name || "—"}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEdit(f)} data-testid={`edit-family-${f.id}`} className="text-ink-muted hover:text-brand" title="Edit"><Pencil size={16} /></button>
                <button onClick={() => remove(f)} data-testid={`delete-family-${f.id}`} className="text-ink-muted hover:text-coral"><Trash2 size={16} /></button>
              </div>
            </div>
            <p className="mt-4 flex items-center gap-2 text-sm text-ink-soft"><Mail size={15} className="text-ink-muted" />{f.email}</p>
            {/@carehub\.app$/.test(f.email) && <p className="mt-1 text-xs text-gold-dark" data-testid={`demo-email-${f.id}`}>Demo address — emails won't arrive. Edit to add their real email.</p>}
            <button onClick={() => welcome(f)} data-testid={`welcome-family-${f.id}`} className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-brand/30 bg-brand-light/40 py-2 text-sm font-medium text-brand hover:bg-brand-light">
              <Send size={14} /> {f.welcome_sent_at ? "Resend welcome email" : "Send welcome email"}
            </button>
            {f.client_id && (
              <Link to={`/family-preview/${f.client_id}`} data-testid={`preview-family-${f.id}`}
                className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-border py-2 text-sm font-medium text-ink transition-colors hover:bg-secondary">
                <Eye size={14} /> Preview their portal
              </Link>
            )}
          </div>
        ))}
        {families.length === 0 && (
          <p className="col-span-full rounded-xl border border-dashed border-border bg-white py-12 text-center text-ink-muted">
            <Users size={26} className="mx-auto mb-2 opacity-40" /> No family logins yet.
          </p>
        )}
      </div>

      {edit && <EditFamily f={edit} clients={clients} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <form onSubmit={create} data-testid="family-form" className="relative z-10 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-ink">Create family login</h2>
              <button type="button" onClick={() => setOpen(false)}><X size={20} className="text-ink-muted" /></button>
            </div>
            <div className="space-y-4">
              <div><label className="mb-1 block text-sm font-medium text-ink">Client *</label>
                <select data-testid="family-client-select" className={input} value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                  <option value="">Select client…</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
              <div><label className="mb-1 block text-sm font-medium text-ink">Family member name *</label>
                <input required data-testid="family-name-input" className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><label className="mb-1 block text-sm font-medium text-ink">Email *</label>
                <input required type="email" data-testid="family-email-input" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><label className="mb-1 block text-sm font-medium text-ink">Temporary password *</label>
                <input required data-testid="family-password-input" className={input} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            </div>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setOpen(false)} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button>
              <button type="submit" data-testid="save-family-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark">Create login</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
